import React, { useState } from 'react';

export interface ChartSeries {
  name: string;
  /** One value per time point; null leaves a gap. */
  values: (number | null)[];
  /** Colour slot: `a` (orange) or `b` (blue), see `--chart-a` / `--chart-b`. */
  slot: 'a' | 'b';
}

interface Props {
  title: string;
  /** Epoch ms per point, oldest first. */
  times: number[];
  series: ChartSeries[];
  /** Fixed top of the y axis; omitted → a "nice" value above the data, never below `floor`. */
  yMax?: number;
  floor?: number;
  format: (v: number) => string;
  /** Text under the plot for the left edge, e.g. "10 min ago". */
  spanLabel: string;
}

/** 1, 2 or 5 × 10^k at or above v. */
const niceMax = (v: number): number => {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
};

const fmtClock = (ms: number) => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * A small time-series line chart in plain SVG (no library). Up to two series on one axis; 2 px lines,
 * recessive grid, y labels in text ink, a crosshair tooltip on hover, and a legend that doubles as the
 * live readout. The plot stretches with its container (`preserveAspectRatio="none"` with
 * non-scaling strokes), so nothing is measured.
 */
export const LineChart: React.FC<Props> = ({ title, times, series, yMax, floor = 0, format, spanLabel }) => {
  const [hover, setHover] = useState<number | null>(null);
  const n = times.length;
  const dataMax = series.reduce((m, s) => s.values.reduce<number>((mm, v) => (v != null && v > mm ? v : mm), m), 0);
  const top = yMax != null && yMax > 0 ? yMax : Math.max(niceMax(dataMax), floor);
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const x = (i: number) => (n <= 1 ? 100 : (i / (n - 1)) * 100);
  const y = (v: number) => 100 - Math.min(100, Math.max(0, (v / top) * 100));
  const path = (values: (number | null)[]) => {
    let d = '';
    let pen = false;
    values.forEach((v, i) => {
      if (v == null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(2)},${y(v).toFixed(2)} `;
      pen = true;
    });
    return d;
  };

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (n === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / Math.max(1, rect.width);
    setHover(Math.round(Math.min(1, Math.max(0, ratio)) * (n - 1)));
  };

  const at = hover != null && hover < n ? hover : n - 1;
  const readout = (s: ChartSeries) => {
    const v = at >= 0 ? s.values[at] : null;
    return v == null ? '–' : format(v);
  };

  return (
    <div className="chart" role="figure" aria-label={title}>
      <div className="chart-head">
        <b>{title}</b>
        <small>{hover != null && at >= 0 ? fmtClock(times[at]) : 'now'}</small>
      </div>
      <div className="chart-plot" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none">
          {ticks.map((t) => (
            <line key={t} className="chart-grid" x1="0" x2="100" y1={100 - t * 100} y2={100 - t * 100} />
          ))}
          {series.map((s) => (
            <path key={s.name} className={`chart-line chart-line--${s.slot}`} d={path(s.values)} />
          ))}
        </svg>
        {ticks.map((t) => (
          <span key={t} className="chart-y" style={{ top: `${100 - t * 100}%` }}>
            {format(t * top)}
          </span>
        ))}
        {hover != null && n > 0 && (
          <>
            <div className="chart-cross" style={{ left: `${x(at)}%` }} />
            <div className="chart-tip" style={x(at) > 60 ? { right: `${100 - x(at) + 1}%` } : { left: `${x(at) + 1}%` }}>
              <div style={{ color: 'var(--ink3)', marginBottom: 2 }}>{fmtClock(times[at])}</div>
              {series.map((s) => (
                <div key={s.name}>
                  <i className={`sw sw--${s.slot}`} /> {s.name} <b>{readout(s)}</b>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="chart-x">
        <span>{spanLabel}</span>
        <span>now</span>
      </div>
      <div className="chart-legend">
        {series.map((s) => (
          <span key={s.name}>
            <i className={`sw sw--${s.slot}`} />
            {s.name} <b>{readout(s)}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

export default LineChart;
